/**
 * docsSearch — client-side token-prefix search over the published help corpus
 * (Day 100b.2). NET-NEW logic: there is no globalSearch tokenizer to mirror
 * (globalSearch.ts is classify/href/rank/group over the SERVER-side global_search
 * FTS RPC). This follows globalSearch's PURE-helper discipline only — framework-
 * free, Supabase-free, unit-tested — so the overlay Docs-tab search box stays a
 * thin shell over tested logic. Title-weighted (titles are the primary search
 * surface — DOC_STANDARDS §7/§8); AND semantics (every query token must hit).
 * minisearch is DEFERRED (the corpus is small; trigger: ~100+ docs OR real
 * search-misses). Operates over the rows from useDocsPages() (1A).
 *
 * Token-PREFIX, not raw substring: each field is tokenized into alphanumeric
 * words and a query token matches as a PREFIX of a doc word. This rejects the
 * MID-word substring collisions that `field.includes(query)` would false-match
 * (e.g. "po" inside "exPOsure", "fee" inside "cofFEE", "order" inside "reorder")
 * while still matching legitimate word-prefixes ("purchas" → "purchase";
 * "fee" → "feedback" — a real prefix, correctly matched).
 */

export interface DocsSearchDoc {
  slug: string;
  title: string;
  body: string;
}

export interface DocsSearchResult {
  slug: string;
  title: string;
  score: number;
}

// A title hit weighs far more than a body hit — titles are the primary search
// surface (DOC_STANDARDS §7/§8).
const TITLE_WEIGHT = 10;
const BODY_WEIGHT = 1;

/** Lowercase + split on any run of non-alphanumeric chars into word tokens. */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Does any doc token have queryToken as a PREFIX? (not a mid-word substring). */
function prefixHit(queryToken: string, docTokens: string[]): boolean {
  return docTokens.some((t) => t.startsWith(queryToken));
}

/**
 * Rank docs for a query. AND semantics: a doc is a result only if EVERY query
 * token prefix-hits some token in its title or body. Score sums TITLE_WEIGHT for
 * a title hit, else BODY_WEIGHT — so title matches rank above body-only matches.
 * Ties break alphabetically by title (stable, order-independent).
 */
export function searchDocs(query: string, docs: DocsSearchDoc[]): DocsSearchResult[] {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];

  const results: DocsSearchResult[] = [];
  for (const doc of docs) {
    const titleTokens = tokenize(doc.title);
    const bodyTokens = tokenize(doc.body);
    let score = 0;
    let allMatched = true;
    for (const qt of queryTokens) {
      const inTitle = prefixHit(qt, titleTokens);
      const inBody = !inTitle && prefixHit(qt, bodyTokens);
      if (!inTitle && !inBody) {
        allMatched = false;
        break;
      }
      score += inTitle ? TITLE_WEIGHT : BODY_WEIGHT;
    }
    if (allMatched) {
      results.push({ slug: doc.slug, title: doc.title, score });
    }
  }
  return results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
}
